import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { ScormType, TrackingKind } from "../config/trackingConfig";
import { ProgressNode, TrackingContent } from "../types";
import { toTrackingHierarchy } from "./trackingHierarchy";

export interface TrackingRequest {
  corporateId: string | number;
  userId: string | number;
  node: ProgressNode;
}

export const buildTrackingParams = ({
  corporateId,
  userId,
  node,
}: TrackingRequest): URLSearchParams => {
  const params = new URLSearchParams({
    corporateId: String(corporateId),
    userId: String(userId),
    activityId: String(node.activityId),
  });
  const { courseId, pathId } = toTrackingHierarchy(node);
  if (courseId != null) params.set("courseId", String(courseId));
  if (pathId != null) params.set("pathId", String(pathId));
  return params;
};

export const buildTrackingKey = (
  kind: TrackingKind,
  request: TrackingRequest,
): string => `${kind}/${buildTrackingParams(request).toString()}`;

const toScoId = (value: unknown): string | null => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  return text === "" ? null : text;
};

const fetchScormContents = async (
  request: TrackingRequest,
): Promise<TrackingContent[]> => {
  const params = buildTrackingParams(request);
  const multi = await fetcher<any>(
    `${ServicesURL.scormIsMultiSco}?${params.toString()}`,
    MethodType.GET,
  );
  const scormType: ScormType = multi?.scormType ?? "SCORM_2004";
  params.set("scormType", scormType);
  const info = await fetcher<any>(
    `${ServicesURL.scormForceSco}?${params.toString()}`,
    MethodType.GET,
  );
  const scos: any[] = Array.isArray(info?.scos) ? info.scos : [];
  const isMulti = multi?.isMultiScorm === true && scos.length > 1;
  return scos.map((raw, index) => ({
    index,
    kind: "scorm",
    scormType,
    isMulti,
    scoId: toScoId(raw?.scoId),
    status: raw?.status ?? null,
    enrollmentStatus: info?.enrollment?.status ?? null,
    raw: { ...raw, enrollment: info?.enrollment },
  }));
};

const fetchXapiContents = async (
  request: TrackingRequest,
): Promise<TrackingContent[]> => {
  const info = await fetcher<any>(
    `${ServicesURL.xapiForce}?${buildTrackingParams(request).toString()}`,
    MethodType.GET,
  );
  return [
    {
      index: 0,
      kind: "xapi",
      status: info?.enrollment?.status ?? null,
      enrollmentStatus: info?.enrollment?.status ?? null,
      statements: Array.isArray(info?.statements) ? info.statements : [],
      activityStates: Array.isArray(info?.activityStates)
        ? info.activityStates.map((state: any, index: number) => ({
            id: String(state?.id ?? index),
            content: state?.content ?? null,
          }))
        : [],
      raw: info ?? {},
    },
  ];
};

export const fetchTrackingContents = (
  kind: TrackingKind,
  request: TrackingRequest,
): Promise<TrackingContent[]> =>
  kind === "scorm" ? fetchScormContents(request) : fetchXapiContents(request);
